import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useViewConfig } from "../../hooks/useViewConfig";
import { getCategoriesBySection } from "../../services/categoryService";
import HomeCategoriesSkeleton from "./HomeCategoriesSkeleton";
import { getIconComponent } from "../../utils/iconHelper";
import "../../styles/homeCategories.css";

/**
 * Component to dynamically fetch and display popular categories on the homepage.
 */
const HomeCategories = () => {
  const { data: configData, loading, error } = useViewConfig("HOME_CATEGORIES");
  const [dbCategories, setDbCategories] = useState([]);

  // Fetch all categories from database on mount to resolve their IDs and sections
  useEffect(() => {
    let isMounted = true;
    const fetchDbCategories = async () => {
      try {
        const data = await getCategoriesBySection();
        if (isMounted) {
          setDbCategories(data || []);
        }
      } catch (err) {
        console.error("Failed to fetch database categories in HomeCategories:", err);
      }
    };
    fetchDbCategories();
    return () => {
      isMounted = false;
    };
  }, []);

  // Error Handling: If API fails, log error, render nothing, keep home working
  if (error) {
    console.error("Failed to load HOME_CATEGORIES configuration:", error);
    return null;
  }

  // Safely extract categories from multiple possible response layouts
  const categories =
    configData?.data?.data?.categories ||
    configData?.data?.categories ||
    configData?.categories ||
    [];

  // Loading State: Render shimmer skeleton placeholders
  if (loading) {
    return (
      <div className="categories-section-wrapper">
        <div className="section-header categories-header">
          <p>POPULAR CATEGORIES</p>
          <p>Shop from our most popular categories</p>
        </div>
        <HomeCategoriesSkeleton />
      </div>
    );
  }

  // Render nothing if configuration is successfully fetched but empty
  if (!categories || categories.length === 0) {
    return null;
  }

  return (
    <div className="categories-section-wrapper">
      <div className="section-header categories-header">
        <p>POPULAR CATEGORIES</p>
        <p>Shop from our most popular categories</p>
      </div>

      <div className="categories-container">
        {categories.map((category, index) => {
          // Resolve icon dynamically, falling back to Package if not found
          const IconComponent = getIconComponent(category.icon);

          // Robust name matching (case-insensitive and stripping spaces/special chars)
          const cleanTitle = category.title
            ? category.title.toLowerCase().trim().replace(/[^a-z0-9]/g, "")
            : "";

          const matchedDbCats = dbCategories.filter((dbCat) => {
            const cleanDbName = dbCat.name
              ? dbCat.name.toLowerCase().trim().replace(/[^a-z0-9]/g, "")
              : "";
            return cleanDbName === cleanTitle;
          });

          // Dynamic route configuration
          const targetRoute = matchedDbCats.length > 0
            ? `/shop?categories=${encodeURIComponent(matchedDbCats.map(c => c._id).join(','))}`
            : `/shop?search=${encodeURIComponent(category.title || "")}`;

          return (
            <Link
              key={category.route || index}
              to={targetRoute}
              className="category-card"
            >
              <div className="category-icon-circle">
                <IconComponent size={32} strokeWidth={1.5} />
              </div>
              <span className="category-title">{category.title}</span>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default HomeCategories;
